/**
 * Network Status Indicator
 * Shows online/offline status and last sync time
 */

import React, { useState, useEffect } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import StorageService from '../services/storageService';

const NetworkStatus = () => {
  const { isOnline, wasOffline } = useOnlineStatus();
  const [syncStats, setSyncStats] = useState({
    pending_orders: 0,
    pending_ledger_transactions: 0,
    last_sync_time: null,
    total_pending: 0
  });
  const [lastSyncDisplay, setLastSyncDisplay] = useState('Never');

  useEffect(() => {
    loadSyncStats();
    
    // Update stats every 30 seconds
    const interval = setInterval(loadSyncStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSyncStats = async () => {
    try {
      const stats = await StorageService.getSyncStats();
      setSyncStats(stats);
      
      if (stats.last_sync_time) {
        const syncTime = new Date(stats.last_sync_time);
        const now = new Date();
        const diffMs = now - syncTime;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) {
          setLastSyncDisplay('Just now');
        } else if (diffMins < 60) {
          setLastSyncDisplay(`${diffMins} min${diffMins > 1 ? 's' : ''} ago`);
        } else {
          const diffHours = Math.floor(diffMins / 60);
          setLastSyncDisplay(`${diffHours} hour${diffHours > 1 ? 's' : ''} ago`);
        }
      } else {
        setLastSyncDisplay('Never');
      }
    } catch (error) {
      console.error('[NetworkStatus] Error loading sync stats:', error);
    }
  };

  // Show prominent message when coming back online
  if (wasOffline && isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-500 text-white px-4 py-2 text-center animate-pulse">
        <span className="font-semibold">🟢 Back Online!</span>
        {syncStats.total_pending > 0 && (
          <span className="ml-2">Syncing {syncStats.total_pending} pending item{syncStats.total_pending > 1 ? 's' : ''}...</span>
        )}
      </div>
    );
  }

  if (isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 px-4 py-1.5 shadow-sm">
        <div className="flex items-center justify-between max-w-7xl mx-auto text-xs">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-medium text-green-700">Online</span>
            </span>
            {lastSyncDisplay !== 'Never' && (
              <span className="text-gray-500">• Last synced {lastSyncDisplay}</span>
            )}
          </div>
          
          {syncStats.total_pending > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
              <span className="text-xs font-medium">{syncStats.total_pending} pending</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Offline
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-500 text-white px-4 py-2 shadow-lg">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
          <span className="font-semibold">Offline Mode</span>
          <span className="text-xs opacity-90">• Data will sync when online</span>
        </div>
        
        {syncStats.total_pending > 0 && (
          <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
            <span className="text-sm font-medium">{syncStats.total_pending} pending sync</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkStatus;
